import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Button, Form, Input, Modal, Table, Space, notification, Popconfirm, Tag, Tooltip, Upload, InputNumber, Switch, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { GroupService } from '../services/groupService';
import { Group } from '../types/group';
import { DishService } from '../services/dishService';
import { Dish } from '../types/dish';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import { parseExcelToGroups, downloadGroupExcelTemplate } from '../utils/groupExcelUtils';
import viVN from 'antd/locale/vi_VN';

const { Search } = Input;
const groupService = new GroupService();
const dishService = new DishService();

const CACHE_TTL = 60 * 1000;

type CacheEntry<T> = {
  data: T[];
  timestamp: number;
};

let groupsCache: CacheEntry<Group> | null = null;
let groupsRequest: Promise<Group[]> | null = null;
let dishesCache: CacheEntry<Dish> | null = null;
let dishesRequest: Promise<Dish[]> | null = null;

const isCacheValid = <T,>(cache: CacheEntry<T> | null) =>
  !!cache && Date.now() - cache.timestamp < CACHE_TTL;

const sortGroups = (list: Group[]) => [...list].sort((a, b) => {
  if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
    return a.displayOrder - b.displayOrder;
  }
  return 0;
});

const GroupManagement: React.FC = () => {
  const isMountedRef = useRef(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [isTourOpen, setIsTourOpen] = useState(false);

  // Tour refs
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const addButtonRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const uploadButtonRef = useRef(null);
  const tableRef = useRef(null);

  // Tour steps
  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý nhóm món',
      description: 'Chào mừng bạn đến với trang Quản lý nhóm món! Đây là nơi bạn có thể tạo và quản lý các nhóm món ăn để phân loại thực đơn một cách khoa học và dễ quản lý.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm nhóm món',
      description: 'Sử dụng thanh tìm kiếm này để tìm nhóm món theo mã hoặc tên nhóm nhanh chóng.',
      target: () => searchRef.current,
    },
    {
      title: 'Thêm nhóm món mới',
      description: 'Nhấn vào đây để tạo một nhóm món mới. Bạn sẽ cần nhập mã nhóm, tên nhóm, thứ tự hiển thị và trạng thái của nhóm.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Tải file mẫu Excel',
      description: 'Tải xuống file mẫu Excel để xem định dạng chuẩn khi cập nhật nhiều nhóm món cùng lúc. File mẫu sẽ có dòng tiêu đề ở đầu và dữ liệu bắt đầu từ dòng thứ 2.',
      target: () => downloadButtonRef.current,
    },
    {
      title: 'Tải lên file Excel',
      description: 'Sử dụng tính năng này để cập nhật hoặc thêm mới nhiều nhóm món cùng lúc thông qua file Excel, giúp tiết kiệm thời gian khi có nhiều nhóm cần quản lý.',
      target: () => uploadButtonRef.current,
    },
    {
      title: 'Bảng danh sách nhóm món',
      description: 'Bảng hiển thị tất cả các nhóm món đã tạo, bao gồm mã nhóm, tên nhóm, thứ tự, trạng thái và các món ăn đang áp dụng nhóm này. Bạn có thể chỉnh sửa hoặc xóa nhóm từ đây.',
      target: () => tableRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể tạo và quản lý các nhóm món để tổ chức thực đơn một cách hiệu quả.',
      target: () => titleRef.current,
    },
  ];

  const updateGroupsState = useCallback((updater: (prev: Group[]) => Group[]) => {
    setGroups((prev) => {
      const next = updater(prev);
      groupsCache = {
        data: next,
        timestamp: Date.now(),
      };
      return next;
    });
  }, []);

  const fetchGroups = useCallback(async (force = false): Promise<Group[]> => {
    if (!force && isCacheValid(groupsCache)) {
      if (isMountedRef.current) {
        setGroups(groupsCache!.data);
      }
      return groupsCache!.data;
    }

    if (groupsRequest) {
      const sharedData = await groupsRequest;
      if (isMountedRef.current) {
        setGroups(sharedData);
      }
      return sharedData;
    }

    if (isMountedRef.current) {
      setLoading(true);
    }

    groupsRequest = groupService.getAllGroups();

    try {
      const fetchedGroups = await groupsRequest;
      const sortedGroups = sortGroups(fetchedGroups);
      groupsCache = {
        data: sortedGroups,
        timestamp: Date.now(),
      };
      if (isMountedRef.current) {
        setGroups(sortedGroups);
      }
      return sortedGroups;
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (isMountedRef.current) {
        notification.error({
          message: 'Lỗi',
          description: 'Không thể tải danh sách nhóm món.',
        });
      }
      return [];
    } finally {
      groupsRequest = null;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchDishes = useCallback(async (force = false): Promise<Dish[]> => {
    if (!force && isCacheValid(dishesCache)) {
      if (isMountedRef.current) {
        setDishes(dishesCache!.data);
      }
      return dishesCache!.data;
    }

    if (dishesRequest) {
      const sharedData = await dishesRequest;
      if (isMountedRef.current) {
        setDishes(sharedData);
      }
      return sharedData;
    }

    dishesRequest = dishService.getAllDishes();

    try {
      const fetchedDishes = await dishesRequest;
      dishesCache = {
        data: fetchedDishes,
        timestamp: Date.now(),
      };
      if (isMountedRef.current) {
        setDishes(fetchedDishes);
      }
      return fetchedDishes;
    } catch (error) {
      console.error('Error fetching dishes:', error);
      return [];
    } finally {
      dishesRequest = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void Promise.all([fetchGroups(), fetchDishes()]);

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchDishes, fetchGroups]);

  const handleAddOrUpdateGroup = async (values: Group) => {
    try {
      if (editingGroup) {
        await groupService.updateGroup(editingGroup.id!, values);
        updateGroupsState((prev) => sortGroups(prev.map((group) => (
          group.id === editingGroup.id
            ? { ...group, ...values }
            : group
        ))));
        notification.success({
          message: 'Thành công',
          description: 'Nhóm món đã được cập nhật thành công!',
        });
      } else {
        const createdGroup = await groupService.addGroup(values);
        updateGroupsState((prev) => sortGroups([createdGroup, ...prev]));
        notification.success({
          message: 'Thành công',
          description: 'Nhóm món mới đã được thêm thành công!',
        });
      }
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving group:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể lưu nhóm món.',
      });
    }
  };

  const handleEdit = (group: Group) => {
    setEditingGroup(group);
    form.setFieldsValue(group);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    // Check if any dishes are using this group
    const appliedDishes = dishes.filter(dish => dish.group === id);

    if (appliedDishes.length > 0) {
      notification.error({
        message: 'Không thể xóa',
        description: `Nhóm món này đang được sử dụng bởi ${appliedDishes.length} món ăn. Vui lòng thay đổi nhóm của các món ăn trước khi xóa.`,
      });
      return;
    }

    try {
      await groupService.deleteGroup(id);
      updateGroupsState((prev) => prev.filter((group) => group.id !== id));
      notification.success({
        message: 'Thành công',
        description: 'Nhóm món đã được xóa thành công!',
      });
    } catch (error) {
      console.error('Error deleting group:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa nhóm món.',
      });
    }
  };

  const handleDeleteAll = async () => {
    if (groups.length === 0) {
      notification.info({
        message: 'Không có dữ liệu',
        description: 'Hiện không có nhóm món nào để xóa.',
      });
      return;
    }

    const usedGroupIds = new Set(dishes.map((dish) => dish.group).filter(Boolean));
    const deletableGroups = groups.filter((group) => group.id && !usedGroupIds.has(group.id));
    const blockedCount = groups.length - deletableGroups.length;

    if (deletableGroups.length === 0) {
      notification.error({
        message: 'Không thể xóa',
        description: 'Tất cả nhóm món hiện đang được món ăn sử dụng.',
      });
      return;
    }

    setDeletingAll(true);
    try {
      const results = await Promise.allSettled(
        deletableGroups.map((group) => groupService.deleteGroup(group.id!))
      );

      const deletedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - deletedCount;

      if (deletedCount > 0) {
        const deletedIds = new Set(
          deletableGroups
            .filter((_, index) => results[index].status === 'fulfilled')
            .map((group) => group.id)
        );
        updateGroupsState((prev) => prev.filter((group) => !group.id || !deletedIds.has(group.id)));
      }

      if (failedCount === 0) {
        notification.success({
          message: 'Thành công',
          description: blockedCount > 0
            ? `Đã xóa ${deletedCount} nhóm món. Bỏ qua ${blockedCount} nhóm đang được sử dụng.`
            : `Đã xóa ${deletedCount} nhóm món.`,
        });
      } else {
        notification.warning({
          message: 'Xóa chưa hoàn tất',
          description: `Đã xóa ${deletedCount} nhóm món, thất bại ${failedCount}. ${blockedCount > 0 ? `Bỏ qua ${blockedCount} nhóm đang được sử dụng.` : ''}`,
        });
      }
    } catch (error) {
      console.error('Error deleting all groups:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa tất cả nhóm món.',
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleAddNew = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const dishNamesByGroupId = useMemo(() => {
    const map = new Map<string, Dish[]>();
    dishes.forEach((dish) => {
      if (!dish.group) return;
      const current = map.get(dish.group) || [];
      current.push(dish);
      map.set(dish.group, current);
    });
    return map;
  }, [dishes]);

  const handleToggleGroupActive = useCallback(async (record: Group, checked: boolean) => {
    if (!record.id) return;

    updateGroupsState((prev) => prev.map((group) => (
      group.id === record.id ? { ...group, isActive: checked } : group
    )));

    try {
      await groupService.updateGroup(record.id, { ...record, isActive: checked });
    } catch (error) {
      updateGroupsState((prev) => prev.map((group) => (
        group.id === record.id ? { ...group, isActive: !checked } : group
      )));
      notification.error({
        message: 'Lỗi',
        description: 'Không thể cập nhật trạng thái nhóm món.',
      });
    }
  }, [updateGroupsState]);

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: 'Mã nhóm',
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: 'Tên nhóm',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 100,
    },
    {
      title: 'Trạng thái',
      key: 'isActive',
      dataIndex: 'isActive',
      width: 120,
      render: (isActive: boolean, record: any) => (
        <Switch
          checkedChildren="Hiển thị"
          unCheckedChildren="Ẩn"
          checked={isActive}
          onChange={(checked) => handleToggleGroupActive(record, checked)}
        />
      )
    },
    {
      title: 'Món ăn đang áp dụng',
      key: 'appliedDishes',
      width: 400,
      render: (_: any, record: Group) => {
        const appliedDishes = dishNamesByGroupId.get(record.id || '') || [];

        if (appliedDishes.length === 0) {
          return <span style={{ color: '#999' }}>Không có món ăn nào</span>;
        }

        const displayedDishes = appliedDishes.slice(0, 3);
        const remainingDishes = appliedDishes.slice(3);

        return (
          <Space wrap>
            {displayedDishes.map(dish => (
              <Tag color="blue" key={dish.id}>
                {dish.name}
              </Tag>
            ))}
            {remainingDishes.length > 0 && (
              <Tooltip
                title={
                  <div>
                    {remainingDishes.map(dish => (
                      <div key={dish.id}>{dish.name}</div>
                    ))}
                  </div>
                }
              >
                <Tag color="purple">+{remainingDishes.length} món khác</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: Group) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Xóa nhóm món"
            description="Bạn có chắc chắn muốn xóa nhóm món này?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredGroups = useMemo(() => (
    searchTerm
      ? groups.filter(group =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : groups
  ), [groups, searchTerm]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>Quản lý nhóm món</h1>
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
          <Tag color="blue">{groups.length} nhóm món</Tag>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters">
            <Search
              placeholder="Tìm kiếm theo mã hoặc tên nhóm"
              allowClear
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              ref={searchRef}
            />
          </div>

          <Space>
            <Popconfirm
              title="Xóa toàn bộ nhóm món"
              description="Hệ thống sẽ chỉ xóa các nhóm chưa được món ăn sử dụng. Bạn có chắc chắn muốn tiếp tục?"
              onConfirm={handleDeleteAll}
              okText="Xóa hết"
              cancelText="Hủy"
              okButtonProps={{ danger: true, loading: deletingAll }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deletingAll}
                disabled={deletingAll || groups.length === 0}
              >
                Xóa tất cả
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              className="admin-button"
              ref={addButtonRef}
            >
              Thêm nhóm món mới
            </Button>

            <Tooltip title="Tải lên file Excel để cập nhật hoặc thêm mới nhóm món">
              <Upload
                accept=".xlsx, .xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  setLoading(true);
                  parseExcelToGroups(file).then((groups) => {
                    groupService.batchUpdateFromExcel(groups).then((result) => {
                      setLoading(false);
                      notification.success({
                        message: 'Cập nhật thành công',
                        description: `Đã thêm ${result.added} nhóm mới, cập nhật ${result.updated} nhóm. ${result.errors.length > 0 ? `Có ${result.errors.length} lỗi.` : ''}`,
                      });

                      if (result.errors.length > 0) {
                        Modal.error({
                          title: 'Có lỗi xảy ra khi cập nhật',
                          content: (
                            <div>
                              <p>Các lỗi sau đã xảy ra:</p>
                              <ul>
                                {result.errors.map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          ),
                          width: 600,
                        });
                      }

                      void fetchGroups(true);
                    }).catch((error) => {
                      setLoading(false);
                      notification.error({
                        message: 'Lỗi',
                        description: `Không thể cập nhật danh sách nhóm món: ${error.message}`,
                      });
                    });
                  }).catch((error) => {
                    setLoading(false);
                    notification.error({
                      message: 'Lỗi đọc file',
                      description: `Không thể đọc file Excel: ${error.message}`,
                    });
                  });
                  return false;
                }}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  className="admin-button"
                  ref={uploadButtonRef}
                >
                  Tải lên file Excel
                </Button>
              </Upload>
            </Tooltip>
          </Space>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container">
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredGroups}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1500 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Tổng số: ${total} nhóm món`,
            }}
            ref={tableRef}
          />
        </div>
      </div>

      <Modal
        title={editingGroup ? "Sửa thông tin nhóm món" : "Thêm nhóm món mới"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        className="admin-modal"
      >
        <div className="admin-form-container">
          <Form 
            form={form} 
            onFinish={handleAddOrUpdateGroup} 
            layout="vertical"
            initialValues={{ isActive: true }}
          >
            <div className="admin-form-section">
              <Form.Item
                name="code"
                label="Mã nhóm"
                rules={[{ required: true, message: 'Vui lòng nhập mã nhóm!' }]}
              >
                <Input placeholder="Nhập mã nhóm" />
              </Form.Item>

              <Form.Item
                name="name"
                label="Tên nhóm"
                rules={[{ required: true, message: 'Vui lòng nhập tên nhóm!' }]}
              >
                <Input placeholder="Nhập tên nhóm" />
              </Form.Item>

              <Form.Item
                name="displayOrder"
                label="Thứ tự hiển thị"
                rules={[{ required: true, message: 'Vui lòng nhập thứ tự hiển thị!' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} placeholder="Nhập thứ tự hiển thị" />
              </Form.Item>

              <Form.Item
                name="isActive"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="admin-button"
                >
                  {editingGroup ? 'Cập nhật' : 'Thêm mới'}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>
    </AdminLayout>
  );
};

export default GroupManagement; 